import {create} from 'zustand';
import {
    BaseAttributeDefinition,
    DisplayProfile,
    ProfileItem,
    ProfileItemSection,
    ProfileItemType,
    User,
} from '../types';
import {
    addToProfile,
    deleteProfile,
    getProfile,
    GetProfileOptions,
    getProfiles,
    putProfile,
    removeFromProfile,
    sortProfileItems,
} from '../api/profile.ts';
import {replaceList} from './storeUtils.ts';
import {
    UserPreferences,
    useUserPreferencesStore,
} from './userPreferencesStore.ts';
import {deepEquals, logError} from '@alchemy/core';

type State = {
    profiles: DisplayProfile[];
    current: DisplayProfile | undefined;
    nextUrl?: string | undefined;
    loaded: boolean;
    currentLoaded: boolean;
    loading: boolean;
    loadingCurrent: boolean;
    loadingMore: boolean;
    total?: number;
    hasMore: () => boolean;
    load: (params?: GetProfileOptions, force?: boolean) => Promise<void>;
    loadMore: () => Promise<void>;
    storeProfile: (profile: DisplayProfile) => void;
    loadProfile: (id: string) => Promise<DisplayProfile>;
    updateProfile: (profile: DisplayProfile) => void;
    autoSync: () => Promise<void>;
    toggleAutoSync: () => void;
    syncData: () => Promise<void>;
    arePreferencesSynced: (profile: DisplayProfile) => Promise<boolean>;
    updateProfileItem: (profileId: string, data: ProfileItem) => void;
    deleteProfile: (id: string) => void;
    addToCurrent: (items: ProfileItem[]) => void;
    addToList: (profileId: string | undefined, items: ProfileItem[]) => void;
    sortList: (profileId: string, items: string[]) => void;
    toggleDefinition: (definition: BaseAttributeDefinition) => void;
    removeFromProfile: (profileId: string, ids: string[]) => void;
    setCurrent: (Profile: DisplayProfile | undefined) => Promise<void>;
    loadCurrent: (id: string) => Promise<DisplayProfile | undefined>;
    shouldSelectProfile: () => boolean;
};

export const useProfileStore = create<State>((set, get) => ({
    loadingMore: false,
    loaded: false,
    loading: false,
    currentLoaded: false,
    loadingCurrent: false,
    current: undefined,
    profiles: [],

    load: async (params, force) => {
        const currentState = get();
        if ((currentState.loaded || currentState.loading) && !force) {
            return;
        }

        set({
            loading: true,
        });

        const preferences = await useUserPreferencesStore.getState().load();
        const prefProfile = preferences['profile'];

        try {
            const data = await getProfiles(params);
            let current = currentState.current;

            if (prefProfile && !current) {
                try {
                    current = await getProfile(prefProfile);
                } catch (e) {
                    logError(e);
                    current = undefined;
                }
            }

            set(state => {
                const previousCurrent = prefProfile
                    ? state.profiles.find(at => at.id === prefProfile)
                    : undefined;
                const newList = preserveListItems(state.profiles, data.result);
                if (
                    previousCurrent &&
                    !newList.some(i => i.id === previousCurrent?.id)
                ) {
                    newList.concat(previousCurrent);
                }

                return {
                    profiles: newList,
                    total: data.total,
                    loading: false,
                    loaded: true,
                    nextUrl: data.next || undefined,
                    current,
                };
            });
        } catch (e: any) {
            set({loading: false});
            throw e;
        }
    },

    hasMore() {
        return !!get().nextUrl;
    },

    setCurrent: async profile => {
        const applyProfile = (profile: DisplayProfile | null) => {
            return useUserPreferencesStore.getState().applyProfile(profile);
        };

        if (!profile) {
            set({
                current: undefined,
                loadingCurrent: false,
                currentLoaded: false,
            });

            await applyProfile(null);

            return;
        }

        if (get().current?.id === profile.id) {
            return;
        }

        set({
            current: profile,
            loadingCurrent: false,
            currentLoaded: true,
        });
        await applyProfile(profile);
    },

    loadCurrent: async id => {
        const currentState = get();
        if (currentState.loadingCurrent || currentState.current?.id === id) {
            return;
        }

        const data = currentState.profiles.find(l => l.id === id);

        set({
            current: data,
            loadingCurrent: true,
        });

        try {
            const profile = await getProfile(id);
            set({
                current: profile,
                loadingCurrent: false,
                currentLoaded: true,
            });

            return profile;
        } catch (e: any) {
            logError(e);
            get().setCurrent(undefined);
        }
    },

    shouldSelectProfile: () => {
        const {current, loading, profiles} = get();

        if (current) {
            return false;
        }

        if (loading) {
            return true;
        }

        return profiles.length > 1;
    },

    updateProfile: data => {
        set(state => ({
            profiles: state.profiles.map(b => {
                if (b.id === data.id) {
                    return {
                        ...b,
                        ...data,
                    };
                }

                return b;
            }),
            current: state.current?.id === data.id ? data : state.current,
        }));
    },

    autoSync: async () => {
        const s = get();
        if (s.current?.data?.autoSync) {
            await s.syncData();
        }
    },

    toggleAutoSync: () => {
        set(state => {
            const current = state.current;
            if (!current?.data) {
                return {};
            }

            const as = current.data.autoSync;
            const newData = {
                ...current.data,
                autoSync: !as,
            };

            putProfile(state.current!.id, {data: newData});

            useUserPreferencesStore.getState().applyProfile({
                ...current,
                data: newData,
            });

            return {
                current: {
                    ...current,
                    data: newData,
                },
                profiles: state.profiles.map(p => {
                    if (p.id === current.id) {
                        return {
                            ...current,
                            data: newData,
                        };
                    }

                    return p;
                }),
            };
        });
    },

    syncData: async () => {
        const {profile: _profile, ...data} = await useUserPreferencesStore
            .getState()
            .load();

        set(state => {
            if (!state.current) {
                return state;
            }

            putProfile(state.current!.id, {data});

            return {
                profiles: state.profiles.map(b => {
                    if (b.id === state.current!.id) {
                        return {
                            ...b,
                            data,
                        };
                    }

                    return b;
                }),
                current: {
                    ...state.current,
                    data,
                },
            };
        });
    },

    arePreferencesSynced: async profile => {
        const data = await useUserPreferencesStore.getState().load();

        const norm = (d: UserPreferences | undefined) => {
            if (d) {
                return {
                    ...d,
                    profile: undefined,
                };
            }
        };

        return deepEquals(norm(profile.data), norm(data));
    },

    updateProfileItem: (profileId, item) => {
        const replaceItemInList = (
            l: DisplayProfile,
            item: ProfileItem
        ): DisplayProfile => {
            return {
                ...l,
                items: l.items?.map(i => {
                    return i.id === item.id
                        ? {
                              ...i,
                              ...item,
                          }
                        : i;
                }),
            };
        };

        set(state => ({
            profiles: state.profiles.map(l => {
                if (l.id === profileId) {
                    return replaceItemInList(l, item);
                }

                return l;
            }),
            current: state.current
                ? replaceItemInList(state.current, item)
                : undefined,
        }));
    },

    loadMore: async () => {
        const nextUrl = get().nextUrl;
        if (!nextUrl) {
            return;
        }

        set({loadingMore: true});
        try {
            const data = await getProfiles({nextUrl});

            set(state => ({
                profiles: preserveListItems(
                    state.profiles,
                    state.profiles.concat(data.result)
                ),
                total: data.total,
                loadingMore: false,
                nextUrl: data.next || undefined,
            }));
        } catch (e: any) {
            set({loadingMore: false});

            throw e;
        }
    },

    storeProfile(data) {
        set(state => ({
            profiles: [data].concat(state.profiles),
        }));
    },

    deleteProfile: async id => {
        await deleteProfile(id);

        set(state => ({
            profiles: state.profiles.filter(b => b.id !== id),
        }));
        get().setCurrent(undefined);
    },

    toggleDefinition: definition => {
        const state = get();
        const current = state.current;
        const defId = definition.id;

        if (current) {
            const item = current.items!.find(
                i =>
                    i.section === ProfileItemSection.Attributes &&
                    (i.definition === defId || i.key === defId)
            );
            if (item?.id) {
                state.removeFromProfile(current.id, [item.id]);

                return;
            }
        }

        state.addToCurrent([attributeDefinitionToItem(definition)]);
    },

    loadProfile: async (id: string) => {
        try {
            const profile = await getProfile(id!);
            set(state => {
                return {
                    current:
                        state.current?.id === profile.id
                            ? profile
                            : state.current,
                    profiles: replaceList(state.profiles, profile),
                };
            });

            return profile;
        } catch (e: any) {
            const s = get();
            if (s.current?.id === id) {
                s.setCurrent(undefined);
            }
            throw e;
        }
    },

    addToList: async (profileId, items) => {
        try {
            const profile = await addToProfile(profileId, {
                // @ts-expect-error id cannot be undefined
                items: items.map(i => ({
                    ...i,
                    id: isTmpId(i.id ?? '') ? undefined : i.id,
                })),
            });
            set(state => ({
                current: profile,
                profiles: replaceList(state.profiles, profile),
            }));
        } catch (e: any) {
            logError(e);
            if (profileId) {
                set(state => {
                    if (state.current?.id === profileId) {
                        const curr = state.current!;

                        return {
                            current: {
                                ...curr,
                                items: curr.items,
                            },
                        };
                    }

                    return state;
                });
            }
        }
    },

    addToCurrent: async items => {
        const state = get();
        state.addToList(state.current?.id, items);
    },

    sortList: async (profileId, items) => {
        set(p => ({
            profiles: p.profiles.map(b => {
                if (b.id === profileId && b.items) {
                    return getReorderedListItems(b, items);
                }

                return b;
            }),
            current:
                p.current?.id === profileId
                    ? getReorderedListItems(p.current, items)
                    : p.current,
        }));

        await sortProfileItems(profileId, items);
    },

    removeFromProfile: async (profileId, items) => {
        let current: DisplayProfile | undefined = get().current;
        if (current && current.id !== profileId) {
            current = undefined;
        }

        if (current && current.items !== undefined) {
            set({
                current: {
                    ...current,
                    items: current.items.filter(
                        d => !items.some(i => i === d.id)
                    ),
                },
            });
        }

        const itemsToRemove = items.filter(i => !isTmpId(i));
        if (itemsToRemove.length > 0) {
            const profile = await removeFromProfile(profileId, itemsToRemove);
            set(state => ({
                profiles: replaceList(state.profiles, profile),
            }));
        }
    },
}));

/**
 * The profile's owner, when the profile is shared: public, or owned by
 * someone else than the current user.
 */
export function getSharedProfileOwner(
    profile: DisplayProfile,
    currentUserId: string | undefined
): User | undefined {
    if (!profile.owner) {
        return undefined;
    }

    if (profile.public || profile.owner.id !== currentUserId) {
        return profile.owner;
    }

    return undefined;
}

export function attributeDefinitionToItem(
    definition: BaseAttributeDefinition
): ProfileItem {
    const isBI = definition.builtIn;

    return {
        id: tmpIdPrefix + definition.id,
        section: ProfileItemSection.Attributes,
        type: isBI ? ProfileItemType.BuiltIn : ProfileItemType.Definition,
        definition: isBI ? undefined : definition.id,
        key: isBI ? definition.id : undefined,
    };
}

let inc = 1;

function generateId(): string {
    return tmpIdPrefix + (inc++).toString();
}

export function createDivider(title: string): ProfileItem {
    return {
        id: generateId(),
        section: ProfileItemSection.Attributes,
        type: ProfileItemType.Divider,
        key: title,
    };
}

export function createSpacer(): ProfileItem {
    return {
        id: generateId(),
        section: ProfileItemSection.Attributes,
        type: ProfileItemType.Spacer,
    };
}

const tmpIdPrefix = '_tmp_';

export function isTmpId(id: string): boolean {
    return id.startsWith(tmpIdPrefix);
}

export function hasDefinitionInItems(
    items: ProfileItem[],
    id: string
): boolean {
    return items.some(i => i.definition === id || i.key === id);
}

function getReorderedListItems(
    profile: DisplayProfile,
    order: string[]
): DisplayProfile {
    if (!profile.items) {
        return profile;
    }

    return {
        ...profile,
        items: order
            .map(id => profile.items!.find(i => i.id === id))
            .filter(i => !!i),
    };
}

function preserveListItems(
    prev: DisplayProfile[],
    list: DisplayProfile[]
): DisplayProfile[] {
    return list.map(i => {
        const prevItem = prev.find(p => p.id === i.id);
        if (prevItem) {
            return {
                ...i,
                items: prevItem.items ?? i.items,
            };
        }

        return i;
    });
}
